import { differenceInSeconds, format, isToday } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';

import { logTimeSelectionConfirmed } from '@fhs/cart/src/utils/logging-utils';
import { Button, Text, tokens } from '@fhs/ui';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';

import DayPicker from './day-picker';
import PickupTimeModalHeading from './heading';
import NextDayWarning from './next-day-warning';
import TimePicker from './time-picker';
import { ISelectedDate } from './types';
import { useDatePickerOptions } from './use-date-time-picker-options';

export function CartActionPickupTimeSelector({ onClose }: { onClose(): void }) {
  const { fireOrderIn, setFireOrderIn } = useOrderContext();
  const { logRBIEvent } = useCRMEventsContext();

  const {
    datePickerData,
    isLoadingData,
    firstAvailableDate,
    firstOptionFormatted,
    isRestaurantOpen,
    warningMessage,
  } = useDatePickerOptions();

  const [selectedDay, setSelectedDay] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState('');

  const updateFireOrderTime = useCallback(
    (selectedDateTime: Date) => {
      const selectedDate: ISelectedDate = {
        year: selectedDateTime.getFullYear(),
        month: selectedDateTime.getMonth(),
        day: selectedDateTime.getDate(),
        hour: selectedDateTime.getHours(),
        minutes: selectedDateTime.getMinutes(),
      };

      setFireOrderIn(calculateFireOrderInSeconds(selectedDate));
    },
    [setFireOrderIn]
  );

  useEffect(() => {
    if (!datePickerData || !firstAvailableDate) {
      return;
    }

    let initialTime: Date | null = null;
    let initialDayIndex = 0;

    // If there's an existing fireOrderIn, find the closest matching time slot
    if (fireOrderIn) {
      const targetTime = new Date(Date.now() + fireOrderIn * 1000);
      let closestTime: Date | null = null;
      let closestDiff = Infinity;
      let closestDayIndex = 0;

      // Search all days for the closest time slot
      datePickerData.forEach((day, dayIndex) => {
        day.options.forEach(option => {
          const optionTime = option.time;
          const diff = Math.abs(optionTime.getTime() - targetTime.getTime());
          if (diff < closestDiff) {
            closestDiff = diff;
            closestTime = optionTime;
            closestDayIndex = dayIndex;
          }
        });
      });

      if (closestTime) {
        initialTime = closestTime;
        initialDayIndex = closestDayIndex;
      }
    }

    // If no existing selection or couldn't find a match, use first available
    if (!initialTime && firstOptionFormatted) {
      const firstAvailableDay =
        datePickerData.findIndex(
          day => (day?.options ?? []).length > 0 && day?.isUnavailable === false
        ) ?? 0;

      const firstTimeSlot = datePickerData[firstAvailableDay]?.options?.[0]?.time;
      if (firstTimeSlot) {
        initialTime = firstTimeSlot;
        initialDayIndex = firstAvailableDay;
        if (!fireOrderIn) {
          setFireOrderIn(calculateFireOrderInSeconds(firstOptionFormatted));
        }
      }
    }

    if (initialTime) {
      setSelectedSlot(initialTime.toISOString());
      setSelectedDay(initialDayIndex);
    }
  }, [
    datePickerData,
    firstAvailableDate,
    firstOptionFormatted,
    fireOrderIn,
    setFireOrderIn,
  ]);

  const isTodaySelected = useMemo(() => {
    if (!datePickerData?.[selectedDay]?.date) return false;
    const today = format(new Date(), 'yyyy-MM-dd');
    return datePickerData[selectedDay].date === today;
  }, [datePickerData, selectedDay]);

  const shouldShowNextDayWarning =
    isRestaurantOpen && firstAvailableDate && !isToday(firstAvailableDate);

  if (isLoadingData) {
    return (
      <>
        <PickupTimeModalHeading onClose={onClose} />
        <View style={styles.emptyContainer}>
          <ActivityIndicator size="large" color={tokens.colors.$houseRed} />{' '}
          <Text.Ui size="md">Checking available time slots</Text.Ui>
        </View>
      </>
    );
  }

  if (!datePickerData || datePickerData.length === 0) {
    return (
      <>
        <PickupTimeModalHeading onClose={onClose} />
        <View style={styles.emptyContainer}>
          <Text.Ui size="lg">No available time slots</Text.Ui>
        </View>
      </>
    );
  }

  return (
    <>
      <PickupTimeModalHeading onClose={onClose} />

      <NextDayWarning
        shouldShowNextDayWarning={!!shouldShowNextDayWarning}
        firstAvailableDate={firstAvailableDate}
      />

      <DayPicker
        datePickerData={datePickerData}
        selectedDay={selectedDay}
        setSelectedDay={setSelectedDay}
      />

      <TimePicker
        datePickerData={datePickerData}
        selectedDay={selectedDay}
        selectedSlot={selectedSlot}
        setSelectedSlot={setSelectedSlot}
        warningMessage={isTodaySelected ? warningMessage : ''}
        isRestaurantOpen={isRestaurantOpen}
      />

      <View style={styles.container}>
        <Button
          loading={false}
          disabled={!selectedSlot}
          onPress={() => {
            if (selectedSlot) {
              const selectedDateTime = new Date(selectedSlot);
              const formattedTime = format(selectedDateTime, 'PPpp');
              updateFireOrderTime(selectedDateTime);
              logTimeSelectionConfirmed(logRBIEvent, formattedTime);
              onClose();
            }
          }}
        >
          <Button.Text>Continue</Button.Text>
        </Button>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingBottom: 18,
    marginHorizontal: 28,
  },

  emptyContainer: {
    padding: 28,
    alignItems: 'center',
    height: 324,
    justifyContent: 'center',
    gap: 13,
  },
});

/**
 * Calculates the difference in seconds between the selected date/time and now.
 * This is used to set the fireOrderIn value for scheduling orders.
 */
function calculateFireOrderInSeconds(dateParam: ISelectedDate): number {
  const formattedDate = new Date(
    dateParam.year,
    dateParam.month,
    dateParam.day,
    dateParam.hour,
    dateParam.minutes
  );

  const now = new Date();
  return differenceInSeconds(formattedDate, now);
}
